/**
 * Błąd z „Testuj" → podkreślenie w edytorze SQL (`diagnostics` pola `SqlField`).
 *
 * Trasy test-sql przy błędzie wykonania z Postgresa zwracają `position` — offset 0-based liczony
 * od pierwszego niebiałego znaku SQL-a (backend: `lib/dictionaries/sql-error-position.ts`).
 * Podkreślamy słowo w tym miejscu, dopóki treść pola jest taka jak w chwili testu — po
 * dowolnej zmianie podkreślenie znika (wskazywałoby złe miejsce). Trasa bez treści błędu
 * (dekretacja zwraca sam kod) dostaje w dymku ogólny komunikat.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import type { SqlEditorDiagnostic } from '@/components/ui/LazySqlEditor';

interface TestMutationLike {
  error: unknown;
  /** `submittedAt` mutacji React Query — zmienia się przy każdym kliknięciu „Testuj". */
  submittedAt: number;
}

/** Pozycja błędu i komunikat z odpowiedzi trasy test-sql (albo `null`). */
export function sqlTestErrorPosition(error: unknown): { position: number; message: string } | null {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data as { position?: unknown; message?: unknown } | undefined;
  if (typeof data?.position !== 'number' || !Number.isInteger(data.position)) return null;
  return {
    position: data.position,
    message: typeof data.message === 'string' ? data.message : '',
  };
}

/** Diagnostyka dla edytora: słowo (identyfikator / liczba) zaczynające się w miejscu błędu. */
export function sqlDiagnosticAt(
  sql: string,
  position: number,
  message: string,
): SqlEditorDiagnostic {
  const leading = sql.length - sql.trimStart().length;
  const from = Math.min(sql.length, leading + position);
  const word = /^[\p{L}\p{N}_$."]+/u.exec(sql.slice(from));
  const to = Math.min(sql.length, from + Math.max(1, word?.[0].length ?? 1));
  return { from: Math.max(0, Math.min(from, to - 1)), to, message };
}

export function useSqlTestDiagnostics(
  sql: string,
  mutation: TestMutationLike,
): SqlEditorDiagnostic[] | undefined {
  const { t } = useTranslation();
  const [testedSql, setTestedSql] = useState<string | null>(null);
  // Treść z chwili kliknięcia „Testuj" (efekt po renderze z nowym `submittedAt`).
  useEffect(() => {
    if (mutation.submittedAt) setTestedSql(sql);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.submittedAt]);

  return useMemo(() => {
    if (testedSql === null || testedSql !== sql) return undefined;
    const found = sqlTestErrorPosition(mutation.error);
    if (!found) return undefined;
    const message = found.message || t('common.sqlField.errorFallback');
    return [sqlDiagnosticAt(sql, found.position, message)];
  }, [sql, testedSql, mutation.error, t]);
}
